import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { clearLastSeenNumber } from '@/hooks/useLastSeenNumber/lastSeenNumberStorage';
import { createRedirectToParam } from '@/hooks/useLogout/helpers';
import { routeMap } from '@/routes/routeMap';
import { useAuthTokenActions } from '@/store/authTokenStore';
import { useModalStore } from '@/store/modalStore';
import { assertUnreachable } from '@/utils/typescript';

type CallbackOptions = {
  afterLogout?: 'navigate-to-login' | 'navigate-to-login-with-return' | 'stay';
};

function useLogout() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { removeAuthToken } = useAuthTokenActions();

  return useCallback(
    (options: CallbackOptions = {}) => {
      const afterLogout = options.afterLogout ?? 'navigate-to-login';

      removeAuthToken();
      clearLastSeenNumber();
      queryClient.clear();
      useModalStore.getState().reset();

      switch (afterLogout) {
        case 'stay':
          return;
        case 'navigate-to-login':
          void navigate(routeMap.login.root, { replace: true });
          return;
        case 'navigate-to-login-with-return': {
          const redirectToParam = createRedirectToParam(location.pathname, location.search);
          void navigate(`${routeMap.login.root}${redirectToParam ? `?${redirectToParam}` : ''}`, {
            replace: true,
          });
          return;
        }
        default:
          assertUnreachable(afterLogout);
      }
    },
    [location, navigate, queryClient, removeAuthToken],
  );
}

export default useLogout;
